import React from 'react';
import type { TravelerFields, TravelerFormValues } from '../../api/reservationInfo';

type Props = {
  travelerFields: TravelerFields;
  traveler: TravelerFormValues;
  travelerErrors: Partial<Record<keyof TravelerFormValues, string>>;
  onTextChange: (field: keyof TravelerFormValues) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectChange: (field: keyof TravelerFormValues) => (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled: boolean;
};

const TravelerSection = ({
  travelerFields,
  traveler,
  travelerErrors,
  onTextChange,
  onSelectChange,
  disabled
}: Props) => {
  const isFieldRequired = (fieldName: string): boolean => {
    return travelerFields.required.includes(fieldName);
  };

  const isFieldVisible = (fieldName: string): boolean => {
    return travelerFields.required.includes(fieldName) || travelerFields.optional.includes(fieldName);
  };

  return (
    <div style={styles.section}>
      <h4 style={styles.sectionTitle}>搭乗者情報</h4>

      {(isFieldVisible('flight_first_name_kana') || isFieldVisible('flight_last_name_kana')) && (
        <>
          <div style={styles.formGroup}>
            <label htmlFor="traveler-flightLastNameKana" style={styles.label}>
              姓（カナ）
              {(isFieldRequired('flight_first_name_kana') || isFieldRequired('flight_last_name_kana')) && (
                <span style={styles.required}>*</span>
              )}
            </label>
            <input
              id="traveler-flightLastNameKana"
              type="text"
              style={{
                ...styles.input,
                ...(travelerErrors.flightLastNameKana ? styles.inputError : {})
              }}
              placeholder="例：ヤマダ"
              value={traveler.flightLastNameKana}
              onChange={onTextChange('flightLastNameKana')}
              disabled={disabled}
            />
            {travelerErrors.flightLastNameKana && (
              <span style={styles.errorText}>{travelerErrors.flightLastNameKana}</span>
            )}
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="traveler-flightFirstNameKana" style={styles.label}>
              名（カナ）
              {(isFieldRequired('flight_first_name_kana') || isFieldRequired('flight_last_name_kana')) && (
                <span style={styles.required}>*</span>
              )}
            </label>
            <input
              id="traveler-flightFirstNameKana"
              type="text"
              style={{
                ...styles.input,
                ...(travelerErrors.flightFirstNameKana ? styles.inputError : {})
              }}
              placeholder="例：タロウ"
              value={traveler.flightFirstNameKana}
              onChange={onTextChange('flightFirstNameKana')}
              disabled={disabled}
            />
            {travelerErrors.flightFirstNameKana && (
              <span style={styles.errorText}>{travelerErrors.flightFirstNameKana}</span>
            )}
          </div>
        </>
      )}

      {isFieldVisible('flight_birthday') && (
        <div style={styles.formGroup}>
          <label htmlFor="traveler-flightBirthday" style={styles.label}>
            生年月日
            {isFieldRequired('flight_birthday') && <span style={styles.required}>*</span>}
          </label>
          <input
            id="traveler-flightBirthday"
            type="date"
            style={{
              ...styles.input,
              ...(travelerErrors.flightBirthday ? styles.inputError : {})
            }}
            value={traveler.flightBirthday}
            onChange={onTextChange('flightBirthday')}
            disabled={disabled}
          />
          {travelerErrors.flightBirthday && <span style={styles.errorText}>{travelerErrors.flightBirthday}</span>}
        </div>
      )}

      {isFieldVisible('flight_gender') && (
        <div style={styles.formGroup}>
          <label htmlFor="traveler-flightGender" style={styles.label}>
            性別
            {isFieldRequired('flight_gender') && <span style={styles.required}>*</span>}
          </label>
          <select
            id="traveler-flightGender"
            style={{
              ...styles.select,
              ...(travelerErrors.flightGender ? styles.inputError : {})
            }}
            value={traveler.flightGender}
            onChange={onSelectChange('flightGender')}
            disabled={disabled}
          >
            <option value="">選択してください</option>
            <option value="male">男性</option>
            <option value="female">女性</option>
          </select>
          {travelerErrors.flightGender && <span style={styles.errorText}>{travelerErrors.flightGender}</span>}
        </div>
      )}

      {isFieldVisible('flight_tel') && (
        <div style={styles.formGroup}>
          <label htmlFor="traveler-flightTel" style={styles.label}>
            緊急連絡先
            {isFieldRequired('flight_tel') && <span style={styles.required}>*</span>}
          </label>
          <input
            id="traveler-flightTel"
            type="tel"
            style={{
              ...styles.input,
              ...(travelerErrors.flightTel ? styles.inputError : {})
            }}
            placeholder="例：090-1234-5678"
            value={traveler.flightTel}
            onChange={onTextChange('flightTel')}
            disabled={disabled}
          />
          {travelerErrors.flightTel && <span style={styles.errorText}>{travelerErrors.flightTel}</span>}
        </div>
      )}

      {isFieldVisible('air_seat') && (
        <div style={styles.formGroup}>
          <label htmlFor="traveler-airSeat" style={styles.label}>
            座席の希望
            {isFieldRequired('air_seat') && <span style={styles.required}>*</span>}
          </label>
          <select
            id="traveler-airSeat"
            style={styles.select}
            value={traveler.airSeat}
            onChange={onSelectChange('airSeat')}
            disabled={disabled}
          >
            <option value="">希望なし</option>
            <option value="window">窓側</option>
            <option value="aisle">通路側</option>
          </select>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #ddd'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '12px',
    marginTop: 0,
    color: '#555'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '6px',
    color: '#333'
  },
  required: {
    color: '#e74c3c',
    marginLeft: '4px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    outline: 'none',
    backgroundColor: '#fff',
    cursor: 'pointer'
  },
  inputError: {
    borderColor: '#e74c3c'
  },
  errorText: {
    color: '#e74c3c',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  }
};

export default TravelerSection;
